import React, { useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Alert,
  TableSortLabel,
  TextField,
  FormControlLabel,
  Switch,
  Chip,
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  Star as StarIcon,
  CompareArrows as CompareIcon,
  ArrowDownward as LowIcon,
  ArrowUpward as HighIcon,
  OpenInNew as OpenInNewIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { stockService } from '../../services/stockService';

interface PriceComparison {
  symbol: string;
  name: string;
  start_price: number;
  end_price: number;
  low_price: number;
  high_price: number;
  change_pct: number;
  drawdown_pct: number;
  max_gain_pct: number;
  upside_potential: number;
  distance_from_low: number;
  is_positive: boolean;
  near_low: boolean;
  near_high: boolean;
}

interface ComparisonSummary {
  positive_count: number;
  negative_count: number;
  avg_change_pct: number;
  max_gain_pct: number;
  max_loss_pct: number;
  near_low_count: number;
  near_high_count: number;
}

type SortField = 'change_pct' | 'symbol' | 'name' | 'start_price' | 'end_price' | 'low_price' | 'high_price' | 'upside_potential' | 'distance_from_low' | 'status';
type SortOrder = 'asc' | 'desc';

const getScreenerSymbol = (symbol: string): string => {
  return symbol.replace('.NS', '').replace('.BO', '');
};

const getStatusSortValue = (comparison: PriceComparison): number => {
  if (comparison.near_low) return 2;
  if (comparison.near_high) return 1;
  return 0;
};

const DateRangeComparisonTab: React.FC = () => {
  const navigate = useNavigate();
  const [comparisons, setComparisons] = useState<PriceComparison[]>([]);
  const [summary, setSummary] = useState<ComparisonSummary | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Date inputs
  const today = new Date().toISOString().split('T')[0];
  const oneMonthAgo = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
  const [startDate, setStartDate] = useState(oneMonthAgo);
  const [endDate, setEndDate] = useState(today);
  
  // Filters
  const [favoritesOnly, setFavoritesOnly] = useState(true);
  const [symbolFilter, setSymbolFilter] = useState('');
  
  // Sorting
  const [sortField, setSortField] = useState<SortField>('upside_potential');
  const [sortOrder, setSortOrder] = useState<SortOrder>('desc');
  
  const [totalStocks, setTotalStocks] = useState(0);
  const [hasSearched, setHasSearched] = useState(false);

  const loadComparison = async () => {
    if (!startDate || !endDate) {
      setError('Please select both start and end dates');
      return;
    }
    
    if (new Date(startDate) >= new Date(endDate)) {
      setError('Start date must be before end date');
      return;
    }
    
    setLoading(true);
    setError(null);
    setHasSearched(true);
    
    try {
      const response = await stockService.getDateRangeComparison({
        start_date: startDate,
        end_date: endDate,
        favorites_only: favoritesOnly,
        sort_by: sortField,
        sort_order: sortOrder,
        limit: 500,
      });
      
      setComparisons(response.comparisons || []);
      setSummary(response.summary || null);
      setTotalStocks(response.total_stocks_analyzed || 0);
      
      if (response.message) {
        setError(response.message);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load comparison data');
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (field: SortField) => {
    if (field === sortField) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('desc');
    }
  };

  const filteredComparisons = comparisons.filter((c) => {
    if (!symbolFilter.trim()) return true;
    const pattern = symbolFilter.trim();
    try {
      // Support glob patterns: * → .*, ? → ., or direct regex
      const isGlob = /^[A-Za-z0-9.*?&_-]+$/.test(pattern) && (pattern.includes('*') || pattern.includes('?'));
      let regex: RegExp;
      if (isGlob) {
        const escaped = pattern.replace(/[.+^${}()|[\]\\]/g, '\\$&').replace(/\*/g, '.*').replace(/\?/g, '.');
        regex = new RegExp(`^${escaped}$`, 'i');
      } else {
        regex = new RegExp(pattern, 'i');
      }
      return regex.test(c.symbol);
    } catch {
      // If invalid regex, fall back to simple substring match
      return c.symbol.toLowerCase().includes(pattern.toLowerCase());
    }
  });

  const sortedComparisons = [...filteredComparisons].sort((a, b) => {
    const multiplier = sortOrder === 'asc' ? 1 : -1;
    switch (sortField) {
      case 'change_pct':
        return (a.change_pct - b.change_pct) * multiplier;
      case 'symbol':
        return a.symbol.localeCompare(b.symbol) * multiplier;
      case 'name':
        return a.name.localeCompare(b.name) * multiplier;
      case 'start_price':
        return (a.start_price - b.start_price) * multiplier;
      case 'end_price':
        return (a.end_price - b.end_price) * multiplier;
      case 'low_price':
        return (a.low_price - b.low_price) * multiplier;
      case 'high_price':
        return (a.high_price - b.high_price) * multiplier;
      case 'upside_potential':
        return (a.upside_potential - b.upside_potential) * multiplier;
      case 'distance_from_low':
        return (a.distance_from_low - b.distance_from_low) * multiplier;
      case 'status':
        return (getStatusSortValue(a) - getStatusSortValue(b)) * multiplier;
      default:
        return 0;
    }
  });

  const formatPercentage = (value: number, showSign: boolean = true) => {
    const sign = showSign && value > 0 ? '+' : '';
    return `${sign}${value.toFixed(2)}%`;
  };

  const formatPrice = (value: number) => {
    return `₹${value.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  // Quick date presets
  const setDatePreset = (preset: string) => {
    const now = new Date();
    let start: Date;
    
    switch (preset) {
      case '1w':
        start = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
        break;
      case '1m':
        start = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
        break;
      case '3m':
        start = new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000);
        break;
      case '6m':
        start = new Date(now.getTime() - 180 * 24 * 60 * 60 * 1000);
        break;
      case '1y':
        start = new Date(now.getTime() - 365 * 24 * 60 * 60 * 1000);
        break;
      case 'ytd':
        start = new Date(now.getFullYear(), 0, 1);
        break;
      default:
        return;
    }
    
    setStartDate(start.toISOString().split('T')[0]);
    setEndDate(now.toISOString().split('T')[0]);
  };

  return (
    <Box>
      {/* Controls */}
      <Card sx={{ mb: 1 }}>
        <CardContent sx={{ py: 1, '&:last-child': { pb: 1 } }}>
          <Grid container spacing={1} alignItems="center">
            <Grid item xs={6} md={2}>
              <TextField
                label="Start Date"
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                fullWidth
                InputLabelProps={{ shrink: true }}
                size="small"
              />
            </Grid>
            <Grid item xs={6} md={2}>
              <TextField
                label="End Date"
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                fullWidth
                InputLabelProps={{ shrink: true }}
                size="small"
              />
            </Grid>
            <Grid item xs={6} md={2}>
              <FormControlLabel
                control={
                  <Switch
                    checked={favoritesOnly}
                    onChange={(e) => setFavoritesOnly(e.target.checked)}
                    color="primary"
                  />
                }
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <StarIcon sx={{ fontSize: 16, color: favoritesOnly ? 'gold' : 'grey.500' }} />
                    <span>Favorites Only</span>
                  </Box>
                }
              />
            </Grid>
            <Grid item xs={6} md={2}>
              <TextField
                label="Symbol Filter"
                value={symbolFilter}
                onChange={(e) => setSymbolFilter(e.target.value)}
                fullWidth
                size="small"
                placeholder="*.NS, TATA*, regex"
                helperText={symbolFilter ? `${sortedComparisons.length} match` : ''}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <Button
                variant="contained"
                onClick={loadComparison}
                disabled={loading}
                startIcon={loading ? <CircularProgress size={20} /> : <CompareIcon />}
                fullWidth
              >
                Compare Prices
              </Button>
            </Grid>
          </Grid>
          
          {/* Quick presets */}
          <Box sx={{ mt: 0.5, display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
            <Typography variant="body2" sx={{ mr: 1, alignSelf: 'center' }}>Quick:</Typography>
            {['1w', '1m', '3m', '6m', '1y', 'ytd'].map((preset) => (
              <Chip
                key={preset}
                label={preset.toUpperCase()}
                size="small"
                onClick={() => setDatePreset(preset)}
                variant="outlined"
              />
            ))}
          </Box>
        </CardContent>
      </Card>

      {/* Error */}
      {error && (
        <Alert severity={comparisons.length > 0 ? 'info' : 'warning'} sx={{ mb: 1, py: 0 }}>
          {error}
        </Alert>
      )}

      {/* Summary Stats */}
      {summary && hasSearched && comparisons.length > 0 && (
        <Grid container spacing={1} sx={{ mb: 1 }}>
          <Grid item xs={4} md={2}>
            <Card>
              <CardContent sx={{ py: 0.5, textAlign: 'center', '&:last-child': { pb: 0.5 } }}>
                <Typography variant="h6" color="success.main">{summary.positive_count}</Typography>
                <Typography variant="caption" color="text.secondary">Gainers</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={4} md={2}>
            <Card>
              <CardContent sx={{ py: 0.5, textAlign: 'center', '&:last-child': { pb: 0.5 } }}>
                <Typography variant="h6" color="error.main">{summary.negative_count}</Typography>
                <Typography variant="caption" color="text.secondary">Losers</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={4} md={2}>
            <Card>
              <CardContent sx={{ py: 0.5, textAlign: 'center', '&:last-child': { pb: 0.5 } }}>
                <Typography variant="h6" color={summary.avg_change_pct >= 0 ? 'success.main' : 'error.main'}>
                  {formatPercentage(summary.avg_change_pct)}
                </Typography>
                <Typography variant="caption" color="text.secondary">Avg Change</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={4} md={2}>
            <Card sx={{ bgcolor: 'rgba(76, 175, 80, 0.1)' }}>
              <CardContent sx={{ py: 0.5, textAlign: 'center', '&:last-child': { pb: 0.5 } }}>
                <Typography variant="h6" color="success.main">{summary.near_low_count}</Typography>
                <Typography variant="caption" color="text.secondary">Near Period Low</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={4} md={2}>
            <Card>
              <CardContent sx={{ py: 0.5, textAlign: 'center', '&:last-child': { pb: 0.5 } }}>
                <Typography variant="h6" color="warning.main">{summary.near_high_count}</Typography>
                <Typography variant="caption" color="text.secondary">Near Period High</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={4} md={2}>
            <Card>
              <CardContent sx={{ py: 0.5, textAlign: 'center', '&:last-child': { pb: 0.5 } }}>
                <Typography variant="h6">{comparisons.length}</Typography>
                <Typography variant="caption" color="text.secondary">
                  Total Stocks
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Results Table */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : hasSearched && (
        <TableContainer component={Paper} sx={{ '& .MuiTableCell-root': { py: 0.25, px: 0.75, fontSize: '0.8rem' } }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>
                  <TableSortLabel
                    active={sortField === 'symbol'}
                    direction={sortField === 'symbol' ? sortOrder : 'asc'}
                    onClick={() => handleSort('symbol')}
                  >
                    Symbol
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={sortField === 'name'}
                    direction={sortField === 'name' ? sortOrder : 'asc'}
                    onClick={() => handleSort('name')}
                  >
                    Name
                  </TableSortLabel>
                </TableCell>
                <TableCell align="right">
                  <TableSortLabel
                    active={sortField === 'start_price'}
                    direction={sortField === 'start_price' ? sortOrder : 'asc'}
                    onClick={() => handleSort('start_price')}
                  >
                    Start
                  </TableSortLabel>
                </TableCell>
                <TableCell align="right">
                  <TableSortLabel
                    active={sortField === 'low_price'}
                    direction={sortField === 'low_price' ? sortOrder : 'asc'}
                    onClick={() => handleSort('low_price')}
                  >
                    <LowIcon sx={{ fontSize: 14, mr: 0.5, color: 'error.main' }} />
                    Low
                  </TableSortLabel>
                </TableCell>
                <TableCell align="right">
                  <TableSortLabel
                    active={sortField === 'high_price'}
                    direction={sortField === 'high_price' ? sortOrder : 'asc'}
                    onClick={() => handleSort('high_price')}
                  >
                    <HighIcon sx={{ fontSize: 14, mr: 0.5, color: 'success.main' }} />
                    High
                  </TableSortLabel>
                </TableCell>
                <TableCell align="right">
                  <TableSortLabel
                    active={sortField === 'end_price'}
                    direction={sortField === 'end_price' ? sortOrder : 'asc'}
                    onClick={() => handleSort('end_price')}
                  >
                    Current
                  </TableSortLabel>
                </TableCell>
                <TableCell align="right">
                  <TableSortLabel
                    active={sortField === 'change_pct'}
                    direction={sortField === 'change_pct' ? sortOrder : 'asc'}
                    onClick={() => handleSort('change_pct')}
                  >
                    Change %
                  </TableSortLabel>
                </TableCell>
                <TableCell align="right">
                  <TableSortLabel
                    active={sortField === 'upside_potential'}
                    direction={sortField === 'upside_potential' ? sortOrder : 'asc'}
                    onClick={() => handleSort('upside_potential')}
                  >
                    Upside to High
                  </TableSortLabel>
                </TableCell>
                <TableCell align="center">
                  <TableSortLabel
                    active={sortField === 'status'}
                    direction={sortField === 'status' ? sortOrder : 'asc'}
                    onClick={() => handleSort('status')}
                  >
                    Status
                  </TableSortLabel>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedComparisons.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} align="center">
                    <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                      {symbolFilter 
                        ? `No stocks matching "${symbolFilter}"`
                        : favoritesOnly 
                          ? 'No stocks in favorites or no price data available for the selected dates'
                          : 'No price data available for the selected date range'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                sortedComparisons.map((comparison) => (
                  <TableRow 
                    key={comparison.symbol}
                    sx={{ 
                      '&:hover': { backgroundColor: 'action.hover' },
                      backgroundColor: comparison.near_low 
                        ? 'rgba(76, 175, 80, 0.08)' 
                        : comparison.near_high 
                          ? 'rgba(255, 152, 0, 0.08)' 
                          : 'inherit'
                    }}
                  >
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        {comparison.is_positive ? (
                          <TrendingUpIcon sx={{ color: 'success.main', fontSize: 16 }} />
                        ) : (
                          <TrendingDownIcon sx={{ color: 'error.main', fontSize: 16 }} />
                        )}
                        <Typography 
                          variant="body2" 
                          fontWeight="bold"
                          sx={{ 
                            cursor: 'pointer',
                            color: 'primary.main',
                            '&:hover': { textDecoration: 'underline' }
                          }}
                          onClick={() => navigate(`/analysis?symbol=${encodeURIComponent(comparison.symbol)}`)}
                        >
                          {comparison.symbol}
                        </Typography>
                        <a
                          href={`https://www.screener.in/company/${getScreenerSymbol(comparison.symbol)}/`}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{ display: 'flex', alignItems: 'center' }}
                          title="View on Screener.in"
                        >
                          <OpenInNewIcon sx={{ fontSize: 14, color: 'text.secondary', '&:hover': { color: 'primary.main' } }} />
                        </a>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography 
                        variant="caption" 
                        color="text.secondary" 
                        sx={{ 
                          display: 'block', 
                          maxWidth: 140, 
                          overflow: 'hidden', 
                          textOverflow: 'ellipsis', 
                          whiteSpace: 'nowrap' 
                        }}
                        title={comparison.name}
                      >
                        {comparison.name}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2">
                        {formatPrice(comparison.start_price)}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" color="error.main" fontWeight="medium">
                        {formatPrice(comparison.low_price)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {formatPercentage(comparison.drawdown_pct)}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" color="success.main" fontWeight="medium">
                        {formatPrice(comparison.high_price)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {formatPercentage(comparison.max_gain_pct)}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" fontWeight="bold">
                        {formatPrice(comparison.end_price)}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Chip
                        label={formatPercentage(comparison.change_pct)}
                        size="small"
                        color={comparison.is_positive ? 'success' : 'error'}
                        sx={{ fontWeight: 'bold' }}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Typography 
                        variant="body2" 
                        color={comparison.upside_potential > 10 ? 'success.main' : 'text.secondary'}
                        fontWeight={comparison.upside_potential > 10 ? 'bold' : 'normal'}
                      >
                        {formatPercentage(comparison.upside_potential, false)}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      {comparison.near_low ? (
                        <Chip 
                          label="Near Low" 
                          size="small" 
                          color="success" 
                          variant="outlined"
                          sx={{ fontSize: '0.7rem' }}
                        />
                      ) : comparison.near_high ? (
                        <Chip 
                          label="Near High" 
                          size="small" 
                          color="warning" 
                          variant="outlined"
                          sx={{ fontSize: '0.7rem' }}
                        />
                      ) : (
                        <Typography variant="caption" color="text.secondary">-</Typography>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
          
          {sortedComparisons.length > 0 && (
            <Box sx={{ p: 1, borderTop: 1, borderColor: 'divider' }}>
              <Typography variant="caption" color="text.secondary">
                Showing {sortedComparisons.length}{symbolFilter ? ` of ${comparisons.length}` : ''} stocks
                {favoritesOnly && ' (Favorites only)'}
                {symbolFilter && ` • Filter: ${symbolFilter}`}
                {' • '}
                <span style={{ color: '#4caf50' }}>Green highlight = Near period low (potential buy)</span>
                {' • '}
                <span style={{ color: '#ff9800' }}>Orange highlight = Near period high</span>
              </Typography>
            </Box>
          )}
        </TableContainer>
      )}
    </Box>
  );
};

export default DateRangeComparisonTab;
